import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { HttpResponse } from '../../../application/types/HttpResponse';
import {
  DashboardUser,
  PanelLog,
  SaveUserRequest,
  UserPermissionKey,
  UserPermissions,
  UserRole,
} from '../../types/User';
import { UserManagementApiService } from '../../services/user-management-api/UserManagementApiService';

interface RoleOption {
  value: UserRole;
  label: string;
  description: string;
}

interface PermissionOption {
  key: UserPermissionKey;
  label: string;
}

interface UserForm {
  username: string;
  displayName: string;
  telegramId: string;
  role: UserRole;
  isActive: boolean;
  password: string;
  permissions: UserPermissions;
}

@Component({
  selector: 'app-users-page',
  standalone: false,
  templateUrl: './users-page.component.html',
  styleUrl: './users-page.component.sass',
})
export class UsersPageComponent implements OnInit {
  public users: DashboardUser[] = [];
  public me: DashboardUser | null = null;
  public logs: PanelLog[] = [];
  public loading = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  public userModalOpen = false;
  public passwordModalOpen = false;
  public editingUser: DashboardUser | null = null;
  public passwordUser: DashboardUser | null = null;
  public passwordValue = '';
  public userForm: UserForm = this.createEmptyForm();

  public readonly roles: RoleOption[] = [
    {
      value: 'admin',
      label: 'Администратор',
      description: 'Полный доступ',
    },
    {
      value: 'editor',
      label: 'Редактор',
      description: 'Редактирование данных',
    },
    {
      value: 'manager',
      label: 'Менеджер',
      description: 'Ограниченная работа',
    },
    {
      value: 'viewer',
      label: 'Наблюдатель',
      description: 'Только просмотр',
    },
  ];

  public readonly permissions: PermissionOption[] = [
    {
      key: 'analytics.view',
      label: 'Аналитика',
    },
    {
      key: 'clients.view',
      label: 'Клиенты: просмотр',
    },
    {
      key: 'clients.edit',
      label: 'Клиенты: изменение',
    },
    {
      key: 'events.view',
      label: 'События: просмотр',
    },
    {
      key: 'events.edit',
      label: 'События: изменение',
    },
    {
      key: 'orders.view',
      label: 'Заказы: просмотр',
    },
    {
      key: 'orders.edit',
      label: 'Заказы: изменение',
    },
    {
      key: 'finance.view',
      label: 'Финансы: просмотр',
    },
    {
      key: 'finance.edit',
      label: 'Финансы: изменение',
    },
    {
      key: 'directories.edit',
      label: 'Справочники',
    },
    {
      key: 'reports.export',
      label: 'Экспорт отчетов',
    },
  ];

  public constructor(
    private userApi: UserManagementApiService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadPage();
  }

  public get activeUsersCount(): number {
    return this.users.filter((user) => user.isActive).length;
  }

  public get blockedUsersCount(): number {
    return this.users.length - this.activeUsersCount;
  }

  public get adminsCount(): number {
    return this.users.filter((user) => user.role === 'admin').length;
  }

  public get isAdmin(): boolean {
    return this.me?.role === 'admin';
  }

  public getRoleLabel(role: UserRole): string {
    return this.roles.find((item) => item.value === role)?.label ?? role;
  }

  public getUserName(user: DashboardUser): string {
    return user.displayName || user.username;
  }

  public getPermissionCount(user: DashboardUser): number {
    return Object.values(user.permissions ?? {}).filter(Boolean).length;
  }

  public formatDate(value: string | null): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public openCreateUser(): void {
    this.editingUser = null;
    this.userForm = this.createEmptyForm();
    this.userModalOpen = true;
    this.clearMessages();
  }

  public openEditUser(user: DashboardUser): void {
    this.editingUser = user;
    this.userForm = {
      username: user.username,
      displayName: user.displayName ?? '',
      telegramId: user.telegramId ?? '',
      role: user.role,
      isActive: user.isActive,
      password: '',
      permissions: { ...user.permissions },
    };
    this.userModalOpen = true;
    this.clearMessages();
  }

  public closeUserModal(): void {
    this.userModalOpen = false;
    this.editingUser = null;
  }

  public openPasswordModal(user: DashboardUser): void {
    this.passwordUser = user;
    this.passwordValue = '';
    this.passwordModalOpen = true;
    this.clearMessages();
  }

  public closePasswordModal(): void {
    this.passwordModalOpen = false;
    this.passwordUser = null;
    this.passwordValue = '';
  }

  public togglePermission(permission: UserPermissionKey): void {
    this.userForm.permissions = {
      ...this.userForm.permissions,
      [permission]: !this.userForm.permissions[permission],
    };
  }

  public async saveUser(): Promise<void> {
    const request: SaveUserRequest = {
      username: this.userForm.username,
      displayName: this.userForm.displayName || null,
      telegramId: this.userForm.telegramId || null,
      role: this.userForm.role,
      isActive: this.userForm.isActive,
      permissions: this.userForm.permissions,
    };

    if (!this.editingUser) {
      request.password = this.userForm.password;
    }

    const response = this.editingUser
      ? await this.userApi.updateUser(this.editingUser.id, request)
      : await this.userApi.createUser(request);

    const savedUser = this.unwrapResponse(response);
    if (!savedUser) {
      return;
    }

    this.successMessage = this.editingUser ? 'Пользователь обновлен.' : 'Пользователь создан.';
    this.closeUserModal();
    await this.loadPage();
  }

  public async toggleUserActive(user: DashboardUser): Promise<void> {
    const response = await this.userApi.updateUser(user.id, {
      isActive: !user.isActive,
    });
    const updatedUser = this.unwrapResponse(response);
    if (!updatedUser) {
      return;
    }

    this.successMessage = updatedUser.isActive ? 'Пользователь разблокирован.' : 'Пользователь заблокирован.';
    await this.loadPage();
  }

  public async saveUserPassword(): Promise<void> {
    if (!this.passwordUser) {
      return;
    }

    const response = await this.userApi.changeUserPassword(this.passwordUser.id, this.passwordValue);
    if (!this.unwrapResponse(response)) {
      return;
    }

    this.successMessage = 'Пароль пользователя обновлен.';
    this.closePasswordModal();
    await this.loadPage();
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private async loadPage(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const [
        usersResponse,
        meResponse,
        logsResponse,
      ] = await Promise.all([
        this.userApi.getUsers(),
        this.userApi.getMe(),
        this.userApi.getPanelLogs(40),
      ]);

      this.users = this.unwrapResponse(usersResponse) ?? [];
      this.me = this.unwrapResponse(meResponse);
      this.logs = this.unwrapResponse(logsResponse) ?? [];
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }

  private clearMessages(): void {
    this.errorMessage = null;
    this.successMessage = null;
  }

  private createEmptyForm(): UserForm {
    return {
      username: '',
      displayName: '',
      telegramId: '',
      role: 'manager',
      isActive: true,
      password: '',
      permissions: {},
    };
  }
}
